import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { AdminUserSortDirection, AdminUserSortField, AdminUserSummary } from '../types/adminUsers';

type Props = {
  users: AdminUserSummary[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  sortBy: AdminUserSortField;
  sortDirection: AdminUserSortDirection;
  onSortChange: (field: AdminUserSortField) => void;
  onPageChange: (_event: unknown, nextPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onEditUser: (user: AdminUserSummary) => void;
  onToggleActive: (user: AdminUserSummary) => void;
  onPasswordReset: (user: AdminUserSummary) => void;
  onArchiveToggle: (user: AdminUserSummary) => void;
  busyAction: { uid: string; action: string } | null;
};

const formatDateTime = (value: Date | null): string => {
  if (!value) {
    return 'Non disponible';
  }

  return format(value, 'dd/MM/yyyy HH:mm', { locale: fr });
};

const getLastActivityLabel = (user: AdminUserSummary): string => {
  if (user.lastLoginAt) {
    return `Connexion: ${formatDateTime(user.lastLoginAt)}`;
  }

  if (user.lastPasswordResetEmailAt) {
    return `Reset envoyé: ${formatDateTime(user.lastPasswordResetEmailAt)}`;
  }

  if (user.updatedAt) {
    return `Mise à jour: ${formatDateTime(user.updatedAt)}`;
  }

  if (user.lastEmailSentAt) {
    return `Email: ${formatDateTime(user.lastEmailSentAt)}`;
  }

  return 'Aucune activité';
};

const renderStatusChip = (user: AdminUserSummary) => {
  if (user.deleted) {
    return <Chip size="small" label="Archivé" color="default" variant="outlined" sx={{ fontWeight: 500 }} />;
  }

  if (user.active) {
    return (
      <Chip 
        size="small" 
        label="Actif" 
        color="success" 
        variant="filled" 
        sx={{ 
          bgcolor: 'success.lighter', 
          color: 'success.dark',
          fontWeight: 700,
          border: 'none'
        }} 
      />
    );
  }

  return <Chip size="small" label="Inactif" color="warning" variant="outlined" sx={{ fontWeight: 500 }} />;
};

const isBusy = (busyAction: Props['busyAction'], user: AdminUserSummary, action: string) => {
  return busyAction?.uid === user.uid && busyAction?.action === action;
};

export default function UsersTable({
  users,
  loading,
  page,
  rowsPerPage,
  totalCount,
  sortBy,
  sortDirection,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onEditUser,
  onToggleActive,
  onPasswordReset,
  onArchiveToggle,
  busyAction,
}: Props) {
  const renderSortableHeader = (label: string, field: AdminUserSortField) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? sortDirection : 'asc'}
      onClick={() => onSortChange(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{renderSortableHeader('Nom complet', 'fullName')}</TableCell>
              <TableCell>{renderSortableHeader('Email', 'email')}</TableCell>
              <TableCell>{renderSortableHeader('Rôle', 'role')}</TableCell>
              <TableCell>{renderSortableHeader('Statut', 'status')}</TableCell>
              <TableCell>{renderSortableHeader('Créé le', 'createdAt')}</TableCell>
              <TableCell>{renderSortableHeader('Infos utiles', 'lastActivityAt')}</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={`users-skeleton-${index}`}>
                    {Array.from({ length: 7 }).map((__, cellIndex) => (
                      <TableCell key={`users-skeleton-${index}-${cellIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : users.map((user) => (
                  <TableRow key={user.uid} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.phone || 'Téléphone non renseigné'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{user.email || 'Email manquant'}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={user.role} 
                        color={user.role === 'Admin' ? 'primary' : 'default'} 
                        variant={user.role === 'Admin' ? 'filled' : 'outlined'}
                        sx={{ fontWeight: user.role === 'Admin' ? 700 : 500 }}
                      />
                    </TableCell>
                    <TableCell>{renderStatusChip(user)}</TableCell>
                    <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{getLastActivityLabel(user)}</Typography>
                        {user.internalNotes ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {user.internalNotes}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        <Tooltip title="Voir ou modifier">
                          <span>
                            <IconButton size="small" onClick={() => onEditUser(user)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={user.active ? 'Désactiver le compte' : 'Activer le compte'}>
                          <span>
                            <IconButton
                              size="small"
                              color={user.active ? 'warning' : 'success'}
                              onClick={() => onToggleActive(user)}
                              disabled={isBusy(busyAction, user, 'active')}
                            >
                              {user.active ? <ToggleOffOutlinedIcon fontSize="small" /> : <ToggleOnOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Envoyer un reset mot de passe">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onPasswordReset(user)}
                              disabled={isBusy(busyAction, user, 'reset')}
                            >
                              <LockResetOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={user.deleted ? 'Restaurer le compte' : 'Archiver le compte'}>
                          <span>
                            <IconButton
                              size="small"
                              color={user.deleted ? 'success' : 'default'}
                              onClick={() => onArchiveToggle(user)}
                              disabled={isBusy(busyAction, user, 'archive')}
                            >
                              {user.deleted ? <RestoreFromTrashOutlinedIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

            {!loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Aucun utilisateur ne correspond aux filtres.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Ajustez la recherche, les filtres ou activez l’option pour inclure les comptes archivés.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Lignes"
      />
    </Paper>
  );
}